import { Marker, Popup, useMap } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-markercluster";
import { calculatePolygonCenter, customIcon } from "./mapUtils";
import MapMarkerPopup from "./mapMarkerPopup.component";
import { clusterDivStyle } from "../../styles/commonStyles";
import L from "leaflet";
import { useDispatch, useSelector } from "react-redux";
import { setNewPosition } from "../../store/features/customer/marker/markerSlice";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  sensorIsHoverEnd,
  sensorIsHoverStart,
} from "../../store/features/customer/sensor/sensorSlice";
import { isSensorDisconnected } from "../../utils/sensorStatusUtils";

const isValidGpsPosition = (position) =>
  Array.isArray(position) &&
  position.length === 2 &&
  position.every((value) => Number.isFinite(Number(value)));

const getMarkerId = (marker) => marker?.id || marker?.sensorId;

const isSameId = (left, right) =>
  left !== undefined &&
  left !== null &&
  right !== undefined &&
  right !== null &&
  String(left) === String(right);

const MapMarkerCluster = ({ markers, polygonCoordinates }) => {
  const dispatch = useDispatch();
  const map = useMap();

  const temporaryMarker = useSelector((state) => state.marker?.temporaryData);
  const newPosition = useSelector((state) => state.marker?.newPosition);
  const { sensorId, isDraggable } = useSelector(
    (state) => state.sensor?.positionEdit
  );
  const { draggableSensorId } = useSelector((state) => state.dragMode);
  const { hoveredSensorId, status } = useSelector(
    (state) => state.sensor?.isHover
  );

  const markerRefs = useRef({});
  const previousActiveMarkerId = useRef(null);
  const activeMarkerId = draggableSensorId;
  const activeMarker = useMemo(() => {
    if (!activeMarkerId) {
      return null;
    }

    const markerFromList = markers?.find((marker) =>
      isSameId(marker?.id, activeMarkerId)
    );

    if (markerFromList) {
      return markerFromList;
    }

    return isSameId(temporaryMarker?.sensorId, activeMarkerId)
      ? temporaryMarker
      : null;
  }, [activeMarkerId, markers, temporaryMarker]);

  const activeMarkerPosition = useMemo(() => {
    if (!activeMarker) {
      return null;
    }

    if (isValidGpsPosition(newPosition)) {
      return newPosition;
    }

    if (isValidGpsPosition(activeMarker?.gps_position)) {
      return activeMarker.gps_position;
    }

    return calculatePolygonCenter(polygonCoordinates);
  }, [activeMarker, newPosition, polygonCoordinates]);

  const [editingPosition, setEditingPosition] = useState(null);

  const visibleMarkers = useMemo(() => {
    const markerMap = new Map();

    (markers || [])
      .filter((marker) => isValidGpsPosition(marker?.gps_position))
      .forEach((marker) => {
        const markerId = getMarkerId(marker);

        if (!markerId || isSameId(markerId, activeMarkerId)) {
          return;
        }

        markerMap.set(String(markerId), marker);
      });

    return Array.from(markerMap.values());
  }, [activeMarkerId, markers]);

  const markerClusterKey = useMemo(
    () =>
      visibleMarkers
        .map((marker) => {
          const [lat, lng] = marker.gps_position;
          return `${getMarkerId(marker)}:${lat}:${lng}`;
        })
        .join("|"),
    [visibleMarkers]
  );

  useEffect(() => {
    if (status && hoveredSensorId) {
      Object.entries(markerRefs.current).forEach(([id, ref]) => {
        if (ref) {
          if (isSameId(id, hoveredSensorId)) {
            ref.openPopup();
          } else {
            ref.closePopup();
          }
        }
      });
    } else if (!status) {
      Object.values(markerRefs.current).forEach((ref) => {
        if (ref) {
          ref.closePopup();
        }
      });
    }
  }, [hoveredSensorId, status]);

  useEffect(() => {
    if (!activeMarkerId) {
      setEditingPosition(null);
      return;
    }

    if (isValidGpsPosition(activeMarkerPosition)) {
      setEditingPosition(activeMarkerPosition);
    }
  }, [activeMarkerId, activeMarkerPosition]);

  useEffect(() => {
    const currentActiveMarkerId = activeMarkerId ? String(activeMarkerId) : null;

    if (
      currentActiveMarkerId &&
      currentActiveMarkerId !== previousActiveMarkerId.current &&
      isValidGpsPosition(editingPosition) &&
      map &&
      polygonCoordinates?.length > 0
    ) {
      const bounds = L.latLngBounds();

      bounds.extend(editingPosition);

      polygonCoordinates.forEach((coord) => {
        bounds.extend(coord);
      });

      map.fitBounds(bounds, {
        padding: [20, 20],
      });
    }

    previousActiveMarkerId.current = currentActiveMarkerId;
  }, [activeMarkerId, editingPosition, polygonCoordinates, map]);

  const handleEditingDragEnd = (event) => {
    const markerPosition = event.target.getLatLng();
    const nextPosition = [markerPosition.lat, markerPosition.lng];

    setEditingPosition(nextPosition);
    dispatch(setNewPosition(nextPosition));
  };

  return (
    <>
      <MarkerClusterGroup
        key={markerClusterKey}
        chunkedLoading
        spiderfyOnMaxZoom={true}
        disableClusteringAtZoom={20}
        iconCreateFunction={(cluster) => {
          const count = cluster.getChildCount();

          return L.divIcon({
            html: `<div style="${clusterDivStyle}"><span>${count}</span></div>`,
            className: "custom-cluster",
            iconSize: L.point(40, 40),
          });
        }}
      >
        {visibleMarkers.map((marker) => {
          const isHover = status && !isSameId(hoveredSensorId, marker.id);
          const isDisconnected = isSensorDisconnected(marker);

          return (
            <Marker
              ref={(ref) => {
                if (ref) {
                  markerRefs.current[marker.id] = ref;
                } else {
                  delete markerRefs.current[marker.id];
                }
              }}
              key={`${marker?.id}-${marker.gps_position[0]}-${marker.gps_position[1]}`}
              position={marker.gps_position}
              icon={customIcon(
                marker,
                null,
                null,
                null,
                isDisconnected
              )}
              opacity={
                isHover || (isDraggable && !isSameId(marker.id, sensorId))
                  ? 0.6
                  : 1
              }
              eventHandlers={{
                mouseover: (e) => {
                  e.target.openPopup();
                  dispatch(sensorIsHoverStart(marker.id));
                },
                mouseout: (e) => {
                  e.target.closePopup();
                  dispatch(sensorIsHoverEnd());
                },
              }}
            >
              <Popup>
                <MapMarkerPopup marker={marker} />
              </Popup>
            </Marker>
          );
        })}
      </MarkerClusterGroup>
      {activeMarker && isValidGpsPosition(editingPosition) && (
        <Marker
          key={`editing-${activeMarkerId}`}
          position={editingPosition}
          icon={customIcon(
            activeMarker,
            null,
            null,
            null,
            isSensorDisconnected(activeMarker)
          )}
          draggable
          autoPan
          zIndexOffset={1000}
          eventHandlers={{
            dragstart: (event) => {
              event.target.closePopup();
            },
            dragend: handleEditingDragEnd,
          }}
        />
      )}
    </>
  );
};

export default MapMarkerCluster;
